import React, { useState, useEffect } from 'react';

import PropTypes from 'prop-types';
import { FiChevronLeft, FiChevronRight, FiX } from 'react-icons/fi';

import { useTranslation } from 'react-i18next';

import WithBackground from '../Common/WithBackground';

import './style.css';

const UPLOADS_BASE_URL = process.env.REACT_APP_API_URL?.replace(
  '/backend/api',
  '/uploads',
);

const WorkerReviews = ({ bg, overlay }) => {
  const { t, i18n } = useTranslation();
  const [reviewImages, setReviewImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [visibleCount, setVisibleCount] = useState(3);

  // Load reviews gallery from API
  useEffect(() => {
    const loadReviews = async () => {
      try {
        setLoading(true);
        const currentLang = i18n.language || 'pl';
        const apiUrl = process.env.REACT_APP_API_URL;
        const response = await fetch(
          `${apiUrl}/reviews.php?lang=${currentLang}`,
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        if (data.success) {
          const reviewItems = data.data || data.reviews || [];

          const transformedImages = reviewItems.map((item) => ({
            id: item.id,
            src: item.image?.startsWith('http')
              ? item.image
              : `${UPLOADS_BASE_URL}/${item.image}`,
            alt: 'Review image',
          }));

          setReviewImages(
            transformedImages.length > 0
              ? transformedImages
              : getDefaultImages(),
          );
        } else {
          console.error('Reviews API returned error:', data.error);
          setReviewImages(getDefaultImages());
        }
      } catch (error) {
        console.error('Error loading reviews:', error);
        setReviewImages(getDefaultImages());
      } finally {
        setLoading(false);
      }
    };

    loadReviews();
  }, [i18n.language]);

  // Fallback default images
  const getDefaultImages = () => [
    {
      id: 1,
      src: '/api/placeholder/800/600',
      alt: 'Review image',
    },
    {
      id: 2,
      src: '/api/placeholder/800/600',
      alt: 'Review image',
    },
    {
      id: 3,
      src: '/api/placeholder/800/600',
      alt: 'Review image',
    },
    {
      id: 4,
      src: '/api/placeholder/800/600',
      alt: 'Review image',
    },
    {
      id: 5,
      src: '/api/placeholder/800/600',
      alt: 'Review image',
    },
  ];

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = 'auto';
    document.documentElement.style.overflow = 'auto';
  };

  const nextLightboxImage = () => {
    setLightboxIndex((prev) => (prev + 1) % reviewImages.length);
  };

  const prevLightboxImage = () => {
    setLightboxIndex(
      (prev) => (prev - 1 + reviewImages.length) % reviewImages.length,
    );
  };

  const handleShowMore = () => {
    setVisibleCount((prev) => prev + 3);
  };

  const visibleImages = reviewImages.slice(0, visibleCount);
  const remainingImages = reviewImages.length - visibleCount;
  const hasMoreImages = remainingImages > 0;

  const reviewsContent = (
    <section className='worker-reviews'>
      <div className='worker-reviews__container'>
        <div className='worker-reviews__header'>
          <h2
            className='worker-reviews__title'
            data-aos='fade-up'
            data-aos-delay='100'
          >
            {t('workerReviews.title')}
          </h2>
          <p
            className='worker-reviews__subtitle'
            data-aos='fade-up'
            data-aos-delay='200'
          >
            {t('workerReviews.subtitle')}
          </p>
        </div>

        <div
          className='worker-reviews__gallery'
          data-aos='fade-up'
          data-aos-delay='300'
        >
          {loading ? (
            <div className='gallery-loading'>
              <p>{t('common.loading') || 'Loading gallery...'}</p>
            </div>
          ) : reviewImages.length === 0 ? (
            <div className='gallery-empty'>
              <p>{t('workerReviews.noImages') || 'No images available'}</p>
            </div>
          ) : (
            <>
              <div className='gallery-grid'>
                {visibleImages.map((image, index) => (
                  <div
                    key={image.id}
                    className='gallery-grid__item'
                    onClick={() => openLightbox(index)}
                    data-aos='zoom-in'
                    data-aos-delay={100 + index * 50}
                  >
                    <div className='gallery-grid__image-container'>
                      <img
                        src={image.src}
                        alt={image.alt}
                        className='gallery-grid__image'
                      />
                      <div className='gallery-grid__overlay'>
                        <div className='gallery-grid__overlay-content'>
                          <span className='gallery-grid__icon'>🔍</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {hasMoreImages && (
                <div className='gallery-show-more'>
                  <button
                    className='gallery-show-more__btn'
                    onClick={handleShowMore}
                  >
                    {t('workerReviews.showMore')}
                    <span className='gallery-show-more__count'>
                      (+{Math.min(remainingImages, 3)})
                    </span>
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Lightbox Modal */}
      {lightboxOpen && reviewImages.length > 0 && (
        <div className='lightbox' onClick={closeLightbox}>
          <div className='lightbox__container'>
            <button
              className='lightbox__close'
              onClick={closeLightbox}
              aria-label='Close lightbox'
            >
              <FiX />
            </button>

            <button
              className='lightbox__nav lightbox__nav--prev'
              onClick={(e) => {
                e.stopPropagation();
                prevLightboxImage();
              }}
              aria-label='Previous image'
            >
              <FiChevronLeft />
            </button>

            <div
              className='lightbox__content'
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={reviewImages[lightboxIndex].src}
                alt={reviewImages[lightboxIndex].alt}
                className='lightbox__image'
              />
              <div className='lightbox__counter'>
                {lightboxIndex + 1} / {reviewImages.length}
              </div>
            </div>

            <button
              className='lightbox__nav lightbox__nav--next'
              onClick={(e) => {
                e.stopPropagation();
                nextLightboxImage();
              }}
              aria-label='Next image'
            >
              <FiChevronRight />
            </button>
          </div>
        </div>
      )}
    </section>
  );

  return bg ? (
    <WithBackground bg={bg} overlay={overlay}>
      {reviewsContent}
    </WithBackground>
  ) : (
    reviewsContent
  );
};

WorkerReviews.propTypes = {
  bg: PropTypes.string,
  overlay: PropTypes.oneOf(['dark', 'light', 'golden', 'none']),
};

WorkerReviews.defaultProps = {
  bg: null,
  overlay: 'dark',
};

export default WorkerReviews;
